import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useCreateProject,
  type CreateProjectInput,
} from '@/hooks'
import { useTeams } from '@/hooks'
import { cn } from '@/lib/utils'

/**
 * 新建项目页面
 */
export const Route = createFileRoute('/_authenticated/pm/projects/new')({
  component: NewProjectPage,
  validateSearch: (search: Record<string, unknown>) => ({
    teamId: search.teamId as string | undefined,
  }),
})

function NewProjectPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/_authenticated/pm/projects/new' })
  const defaultTeamId = search.teamId
  const createMutation = useCreateProject()
  const { data: teams = [] } = useTeams({})

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    teamId: defaultTeamId || '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) {
      newErrors.name = '请输入项目名称'
    } else if (formData.name.length > 100) {
      newErrors.name = '项目名称不能超过100个字符'
    }
    if (formData.description && formData.description.length > 2000) {
      newErrors.description = '项目描述不能超过2000个字符'
    }
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = '截止日期不能早于开始日期'
    }
    if (!formData.teamId) {
      newErrors.teamId = '请选择所属团队'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) {
      return
    }

    const data: CreateProjectInput = {
      name: formData.name,
      description: formData.description || undefined,
      startDate: formData.startDate ? `${formData.startDate}T00:00:00Z` : undefined,
      endDate: formData.endDate ? `${formData.endDate}T23:59:59Z` : undefined,
      teamId: formData.teamId,
    }
    createMutation.mutate(data, {
      onSuccess: (project) => {
        void navigate({ to: '/pm/projects/$projectId', params: { projectId: project.id } })
      },
    })
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* 返回和标题 */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => void navigate({ to: '/pm/projects' })}
          className="rounded-md p-2 hover:bg-muted"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">新建项目</h1>
          <p className="text-muted-foreground">创建一个新的项目</p>
        </div>
      </div>

      {/* 表单 */}
      <form onSubmit={handleSubmit} className="rounded-lg border bg-card p-6">
        <div className="space-y-4">
          {/* 所属团队 */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">
              所属团队 <span className="text-destructive">*</span>
            </label>
            <Select
              value={formData.teamId || 'placeholder'}
              onValueChange={(value) => setFormData({ ...formData, teamId: value === 'placeholder' ? '' : value })}
            >
              <SelectTrigger className={cn(errors.teamId && 'border-destructive')}>
                <SelectValue placeholder="请选择团队" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="placeholder" disabled>请选择团队</SelectItem>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.teamId && (
              <p className="mt-1 text-sm text-destructive">{errors.teamId}</p>
            )}
          </div>

          {/* 项目名称 */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">
              项目名称 <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="请输入项目名称"
              className={cn(
                'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                errors.name && 'border-destructive'
              )}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          {/* 项目描述 */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">项目描述</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="请输入项目描述"
              rows={4}
              className={cn(
                'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                errors.description && 'border-destructive'
              )}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-destructive">{errors.description}</p>
            )}
          </div>

          {/* 日期 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">开始日期</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">截止日期</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                  errors.endDate && 'border-destructive'
                )}
              />
              {errors.endDate && (
                <p className="mt-1 text-sm text-destructive">{errors.endDate}</p>
              )}
            </div>
          </div>

          {/* 错误提示 */}
          {createMutation.error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {createMutation.error.message}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => void navigate({ to: '/pm/projects' })}
            className="rounded-md px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {createMutation.isPending ? '创建中...' : '创建项目'}
          </button>
        </div>
      </form>
    </div>
  )
}
